<template>
  <div
    ref="root"
    class="st-root"
    :class="{ 'st-compact': density === 'compact', 'st-relaxed': density === 'relaxed', 'st-skew': skew, 'st-dragging': !!drag, 'st-narrow': isNarrow, 'st-collapsed': collapsed, 'st-fit': !!fitRange, 'st-label-tight': labelTight }"
    :style="rootStyle"
    role="region"
    :aria-label="ariaLabel"
  >
    <div v-if="loading" class="st-loading"></div>
    <div
      ref="scroller"
      class="st-scroller"
      @scroll.passive="onScroll"
      @wheel="onWheel"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
      @touchcancel.passive="onTouchEnd"
    >
      <div class="st-canvas" :style="{ width: canvasWidth + 'px' }">
        <div class="st-header" :style="{ width: canvasWidth + 'px', height: headerHeight + 'px' }">
          <div class="st-corner" :style="{ width: effectiveLabelWidth + 'px' }">
            <slot name="corner" :collapsed="collapsed"></slot>
            <template v-if="!fitRange">
              <button type="button" class="st-collapse" :title="collapsed ? expandLabel : collapseLabel" @click="collapsed = !collapsed">
                <i class="fa" :class="collapsed ? 'fa-angle-double-right' : 'fa-angle-double-left'"></i>
              </button>
              <span v-if="!collapsed" class="st-resize-handle" @pointerdown="onLabelResizeStart"></span>
            </template>
          </div>
          <div class="st-scale" :style="{ width: trackWidth + 'px' }">
            <div class="st-scale-groups">
              <div
                v-for="group in groups"
                :key="group.startIndex + '-' + group.date.getTime()"
                class="st-scale-group"
                :style="{ left: group.startIndex * cellWidth + 'px', width: group.count * cellWidth + 'px' }"
              >
                <span class="st-scale-group-label" :style="{ left: effectiveLabelWidth + 'px' }">{{ formatGroup(group.date) }}</span>
              </div>
            </div>
            <div class="st-scale-columns">
              <div
                v-for="(col, i) in columns"
                :key="col.start"
                class="st-scale-col"
                :class="{ 'st-weekend': col.weekend, 'st-today': col.today, 'st-past': col.past }"
                :style="{ left: i * cellWidth + 'px', width: cellWidth + 'px' }"
              >{{ formatColumn(col.date, cellWidth) }}</div>
            </div>
          </div>
        </div>
        <div class="st-body" :style="{ height: layout.total + 'px', width: canvasWidth + 'px' }">
          <div class="st-grid" :style="gridStyle">
            <div
              v-for="stripe in stripes"
              :key="stripe.key"
              class="st-stripe"
              :class="stripe.className"
              :style="{ left: stripe.left + 'px', width: cellWidth + 'px' }"
            ></div>
            <div v-if="nowX !== null" class="st-now" :style="{ left: nowX + 'px' }"></div>
          </div>
          <div
            v-for="item in visibleRows"
            :key="item.row.key"
            class="st-row"
            :class="['st-level-' + item.row.level, { 'st-folder': item.row.hasChildren, 'st-drop-target': drag && drag.toRowKey === item.row.key }, rowClass ? rowClass(item.row) : '']"
            :data-row-index="item.index"
            :style="{ top: item.top + 'px', height: item.height + 'px', width: canvasWidth + 'px' }"
          >
            <div v-if="item.row.custom" class="st-row-custom" :style="{ width: viewportWidth + 'px' }">
              <slot name="row" :row="item.row.row" :width="viewportWidth" :height="item.height"></slot>
            </div>
            <div
              v-if="!item.row.custom"
              class="st-label"
              :style="{ width: effectiveLabelWidth + 'px', paddingLeft: (collapsed ? 4 : 8) + 'px' }"
              :title="item.row.label"
              @click="onLabelClick(item.row, $event)"
            >
              <span class="st-label-content">
                <slot name="label" :row="item.row.row" :flat="item.row" :compact="collapsed || isNarrow">{{ item.row.label }}</slot>
              </span>
              <template v-if="!labelTight">
                <slot name="label-end" :row="item.row.row" :flat="item.row"></slot>
                <span v-if="item.row.hasChildren" class="st-toggle" :class="{ 'st-open': item.row.open }">
                  <i class="fa fa-chevron-down"></i>
                </span>
              </template>
            </div>
            <div v-if="!item.row.custom" class="st-track" :style="{ width: trackWidth + 'px' }" @pointerdown="onTrackPointerDown($event, item.row)" @dblclick="onTrackDblClick($event, item.row)">
              <div
                v-for="p in item.placements"
                :key="p.key"
                class="st-event"
                :class="[eventClass ? eventClass(p.event, item.row) : '', p.event.className || '', {
                  'st-event-dragging': drag && drag.event === p.event,
                  'st-event-draggable': p.event.draggable !== false,
                  'st-clipped-start': p.clippedStart,
                  'st-clipped-end': p.clippedEnd
                }]"
                :style="{ left: p.left + 'px', width: p.width + 'px', top: rowPadding + p.lane * laneHeight + 'px', height: laneHeight - 4 + 'px' }"
                :data-pkey="p.key"
                tabindex="0"
                role="button"
                @pointerdown="onEventPointerDown($event, p.event, item.row.key)"
                @keydown="onEventKey($event, p, item.index)"
                @contextmenu.prevent
              >
                <span
                  v-if="p.event.draggable !== false && p.event.resizable !== false && !p.clippedStart"
                  class="st-handle st-handle-start"
                  @pointerdown.stop="onEventPointerDown($event, p.event, item.row.key, 'start')"
                ></span>
                <div class="st-event-body">
                  <slot name="event" :event="p.event" :row="item.row.row" :placement="p" :cellWidth="cellWidth" :windowStart="windowStartTime">{{ p.event.data && p.event.data.text }}</slot>
                </div>
                <span
                  v-if="p.event.draggable !== false && p.event.resizable !== false && !p.clippedEnd"
                  class="st-handle st-handle-end"
                  @pointerdown.stop="onEventPointerDown($event, p.event, item.row.key, 'end')"
                ></span>
              </div>
            </div>
          </div>
          <div v-if="ghostStyle" class="st-ghost" :class="{ 'st-ghost-create': drag && drag.mode === 'create' }" :style="ghostStyle">
            <span class="st-ghost-label">{{ ghostLabel }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { addUnits, buildColumns, groupColumns, isSameUnit, startOfUnit, timeToX, xToTime } from '~/lib/schedule/timeScale'
import { flattenRows } from '~/lib/schedule/rowTree'
import { indexEventsByRow, layoutRows, rowIndexAt, visibleRowRange } from '~/lib/schedule/eventLayout'
import { usePointerInteractions } from '~/lib/schedule/usePointerInteractions'
import type { DragState, ExtraRow, FlatRow, Placement, RowLayout, TimelineEvent, TimelineRow, TimeUnit } from '~/lib/schedule/types'

const props = withDefaults(defineProps<{
  rows: TimelineRow[]
  events: TimelineEvent[]
  openKeys: string[]
  unit: TimeUnit
  visibleColumns: number
  anchor: Date
  labelWidth?: number
  density?: 'comfortable' | 'compact' | 'relaxed'
  snapMinutes?: number
  longPressMs?: number
  skew?: boolean
  loading?: boolean
  height?: number | string | null
  formatColumn: (date: Date, cellWidth: number) => string
  formatGroup: (date: Date) => string
  formatRange?: (start: Date, end: Date) => string
  canDrop?: (event: TimelineEvent, row: FlatRow) => boolean
  canCreate?: (row: FlatRow) => boolean
  rowClass?: (row: FlatRow) => string
  eventClass?: (event: TimelineEvent, row: FlatRow) => string
  snapRange?: (rowKey: string, start: Date, end: Date, anchor: number) => { start: Date, end: Date } | null
  resizeLimit?: (event: TimelineEvent, edge: 'start' | 'end') => Date | null
  // A fixed range that is fitted to the width instead of the scrolling window
  fitRange?: { start: Date, end: Date } | null
  extraRows?: ExtraRow[]
  ariaLabel?: string
  collapseLabel?: string
  expandLabel?: string
}>(), {
  labelWidth: 300,
  density: 'comfortable',
  snapMinutes: 15,
  longPressMs: 350,
  skew: false,
  loading: false,
  height: null,
  formatRange: undefined,
  canDrop: () => false,
  canCreate: () => false,
  rowClass: undefined,
  eventClass: undefined,
  snapRange: undefined,
  resizeLimit: undefined,
  fitRange: null,
  extraRows: () => [],
  ariaLabel: 'Timeline',
  collapseLabel: 'Collapse',
  expandLabel: 'Expand'
})

const emit = defineEmits<{
  (e: 'update:anchor', date: Date): void
  (e: 'update:labelWidth', width: number): void
  (e: 'range', range: { start: Date, end: Date }): void
  (e: 'toggle', key: string, open: boolean): void
  (e: 'label-click', row: TimelineRow, native: MouseEvent): void
  (e: 'event-click', event: TimelineEvent, rowKey: string, native: Event): void
  (e: 'event-change', drag: DragState): void
  (e: 'range-create', payload: { rowKey: string, start: Date, end: Date }): void
  (e: 'zoom', payload: { direction: 'in' | 'out' }): void
  (e: 'content-height', height: number): void
}>()

const HEADER_TIER = 26
const OVERSCAN = 400
// Recentering or resizing rows while iOS momentum scrolling is running stalls the scroll and makes it jump
const SCROLL_IDLE_MS = 160
const COLLAPSED_WIDTH = 56
// Below this label width the caret and the label-end slot are hidden so the name gets the room
const TIGHT_LABEL_WIDTH = 150
const headerHeight = HEADER_TIER * 2

const root = ref<HTMLElement | null>(null)
const scroller = ref<HTMLElement | null>(null)
const viewportWidth = ref(1000)
const viewportHeight = ref(600)
const rootWidth = ref(1000)
const scrollTop = ref(0)
const collapsed = ref(false)
const windowAnchor = shallowRef<Date>(startOfUnit(props.anchor || new Date(), props.unit))
const now = ref(Date.now())

const laneHeight = computed(() => (props.density === 'compact' ? 28 : props.density === 'relaxed' ? 56 : 40))
const rowPadding = computed(() => (props.density === 'compact' ? 2 : 3))
const isNarrow = computed(() => rootWidth.value < 600)
const labelTight = computed(() => collapsed.value || effectiveLabelWidth.value < TIGHT_LABEL_WIDTH)
const effectiveLabelWidth = computed(() => {
  if (collapsed.value) {
    return COLLAPSED_WIDTH
  }
  if (rootWidth.value < 600) {
    return Math.min(props.labelWidth, 120)
  }
  if (rootWidth.value < 1000) {
    return Math.min(props.labelWidth, 240)
  }
  return props.labelWidth
})
const fitCount = computed(() => {
  const range = props.fitRange
  if (!range) {
    return 0
  }
  let count = 0
  for (let d = startOfUnit(range.start, props.unit); d.getTime() < range.end.getTime(); d = addUnits(d, props.unit, 1)) {
    count++
  }
  return Math.max(1, count)
})
const bufferFor = (visible: number, unit: TimeUnit) => Math.max(visible * 2, unit === 'day' ? 42 : 48)
const buffer = computed(() => (props.fitRange ? 0 : bufferFor(props.visibleColumns, props.unit)))
const windowCount = computed(() => (props.fitRange ? fitCount.value : Math.ceil(props.visibleColumns) + buffer.value * 2))
const minCellWidth = computed(() => (props.fitRange ? 16 : props.unit === 'day' ? 22 : 36))
const cellWidth = computed(() => {
  const available = Math.max(0, viewportWidth.value - effectiveLabelWidth.value)
  return Math.max(minCellWidth.value, available / (props.fitRange ? fitCount.value : props.visibleColumns))
})
const windowStart = computed(() => (props.fitRange ? startOfUnit(props.fitRange.start, props.unit) : addUnits(windowAnchor.value, props.unit, -buffer.value)))
const windowStartTime = computed(() => windowStart.value.getTime())
const columns = computed(() => buildColumns(props.unit, windowStart.value, windowCount.value, new Date(now.value)))
const groups = computed(() => groupColumns(columns.value, props.unit))
const trackWidth = computed(() => columns.value.length * cellWidth.value)
const canvasWidth = computed(() => effectiveLabelWidth.value + trackWidth.value)

const openSet = computed(() => new Set(props.openKeys))
const flatRows = computed(() => {
  const list = flattenRows(props.rows, openSet.value)
  for (const extra of props.extraRows) {
    const index = list.findIndex(r => r.key === extra.afterKey)
    if (index < 0) {
      continue
    }
    list.splice(index + 1, 0, { key: extra.row.key, label: extra.row.label, level: extra.row.level, hasChildren: false, open: false, row: extra.row, height: extra.height, custom: true })
  }
  return list
})
const eventIndex = computed(() => indexEventsByRow(props.events || []))
const liveLayout = computed(() => layoutRows(flatRows.value, eventIndex.value, {
  columns: columns.value,
  cellWidth: cellWidth.value,
  laneHeight: laneHeight.value,
  rowPadding: rowPadding.value,
  minEventWidth: Math.max(6, Math.min(24, cellWidth.value * 0.5))
}))

// Row heights are held while the user scrolls and committed once scrolling is idle
let scrollActive = false
let committedRows: FlatRow[] = flatRows.value
const committedLayout = shallowRef<RowLayout>(liveLayout.value)

const commitLayout = (next: RowLayout) => {
  const prev = committedLayout.value
  if (prev === next) {
    return
  }
  const sameRows = committedRows === flatRows.value
  committedRows = flatRows.value
  committedLayout.value = next
  const el = scroller.value
  if (!el || !sameRows) {
    return
  }
  const top = el.scrollTop
  let index = rowIndexAt(prev, top)
  if (index >= 0 && prev.offsets[index] < top && index + 1 < prev.offsets.length) {
    index++
  }
  const delta = index >= 0 && index < next.offsets.length ? next.offsets[index] - prev.offsets[index] : 0
  if (delta) {
    nextTick(() => {
      el.scrollTop = top + delta
      scrollTop.value = el.scrollTop
    })
  }
}

watch(liveLayout, (next) => {
  if (!scrollActive || committedRows !== flatRows.value || next.heights.length !== committedLayout.value.heights.length) {
    commitLayout(next)
  }
})

const layout = computed<RowLayout>(() => {
  const live = liveLayout.value
  const base = committedLayout.value
  if (base === live || base.heights.length !== live.heights.length) {
    return live
  }
  return { heights: base.heights, offsets: base.offsets, total: base.total, placements: live.placements }
})

watch(() => layout.value.total + headerHeight, (height) => emit('content-height', height), { immediate: true })

const rowRange = computed(() => visibleRowRange(layout.value, scrollTop.value, viewportHeight.value, OVERSCAN).join(':'))

// Placements are rendered only near the horizontal viewport; the bucket keeps re-renders off most scroll frames
const xBucket = ref(0)
const xBucketSize = computed(() => Math.max(200, Math.round(viewportWidth.value / 2)))
const updateXBucket = () => {
  const el = scroller.value
  if (el) {
    xBucket.value = Math.floor(el.scrollLeft / xBucketSize.value)
  }
}

const visibleRows = computed(() => {
  const [first, last] = rowRange.value.split(':').map(Number)
  const minX = xBucket.value * xBucketSize.value - viewportWidth.value
  const maxX = (xBucket.value + 1) * xBucketSize.value + viewportWidth.value * 2
  const items: { row: FlatRow, index: number, top: number, height: number, placements: Placement[] }[] = []
  for (let i = first; i <= last; i++) {
    const row = flatRows.value[i]
    const all = layout.value.placements.get(row.key) || []
    const placements: Placement[] = []
    for (const p of all) {
      if (p.left > maxX) {
        break
      }
      if (p.left + p.width >= minX) {
        placements.push(p)
      }
    }
    items.push({
      row,
      index: i,
      top: layout.value.offsets[i],
      height: layout.value.heights[i],
      placements
    })
  }
  return items
})

const stripes = computed(() => {
  const list: { key: number, left: number, className: string }[] = []
  columns.value.forEach((col, i) => {
    if (col.today && props.unit === 'day') {
      list.push({ key: col.start, left: i * cellWidth.value, className: 'st-stripe-today' })
    } else if (col.weekend) {
      list.push({ key: col.start, left: i * cellWidth.value, className: 'st-stripe-weekend' })
    }
  })
  return list
})

const nowX = computed(() => {
  const cols = columns.value
  if (!cols.length || now.value < cols[0].start || now.value >= cols[cols.length - 1].end) {
    return null
  }
  return timeToX(now.value, cols, cellWidth.value)
})

const gridStyle = computed(() => ({
  left: effectiveLabelWidth.value + 'px',
  width: trackWidth.value + 'px',
  height: layout.value.total + 'px',
  backgroundSize: cellWidth.value + 'px 100%'
}))

const rootStyle = computed(() => {
  if (props.height === null || props.height === undefined) {
    return {}
  }
  return { height: typeof props.height === 'number' ? props.height + 'px' : props.height }
})

const clientXToTrackX = (clientX: number) => {
  const el = scroller.value
  if (!el) {
    return 0
  }
  const rect = el.getBoundingClientRect()
  return clientX - rect.left + el.scrollLeft - effectiveLabelWidth.value
}

const timeAtClientX = (clientX: number) => xToTime(clientXToTrackX(clientX), columns.value, cellWidth.value)

const rowAtClientY = (clientY: number): FlatRow | null => {
  const el = scroller.value
  if (!el) {
    return null
  }
  const rect = el.getBoundingClientRect()
  const y = clientY - rect.top + el.scrollTop - headerHeight
  const index = rowIndexAt(layout.value, y)
  return index >= 0 ? flatRows.value[index] : null
}

const { drag, onEventPointerDown, onTrackPointerDown, onTouchMove, isDragging } = usePointerInteractions({
  scroller,
  unit: () => props.unit,
  snapMinutes: () => props.snapMinutes,
  cellWidth: () => cellWidth.value,
  labelWidth: () => effectiveLabelWidth.value,
  longPressMs: () => props.longPressMs,
  timeAtClientX,
  rowAtClientY,
  canDrop: (event, row) => props.canDrop(event, row),
  canCreate: row => !row.custom && props.canCreate(row),
  snapRange: (rowKey, start, end, anchor) => (props.snapRange ? props.snapRange(rowKey, start, end, anchor) : null),
  resizeLimit: (event, edge) => (props.resizeLimit ? props.resizeLimit(event, edge) : null),
  onEventClick: (event, rowKey, native) => emit('event-click', event, rowKey, native),
  onCommit: (state) => {
    if (state.mode === 'create') {
      emit('range-create', { rowKey: state.toRowKey, start: state.start, end: state.end })
    } else {
      emit('event-change', state)
    }
  }
})

// Double-clicking an empty spot creates a one-slot reservation (1 day daily, 1 hour hourly), alongside drag-create
const onTrackDblClick = (e: MouseEvent, row: FlatRow) => {
  const target = e.target as HTMLElement | null
  if ((target && target.closest('.st-event')) || isDragging() || row.custom || !props.canCreate(row)) {
    return
  }
  const start = startOfUnit(new Date(timeAtClientX(e.clientX)), props.unit)
  emit('range-create', { rowKey: row.key, start, end: addUnits(start, props.unit, 1) })
}

// The preview stays on the pill that was grabbed. A new row uses the lane under the pointer.
const ghostLane = (state: DragState, index: number) => {
  const pad = rowPadding.value
  const laneH = laneHeight.value
  const rowHeight = layout.value.heights[index]
  const maxLane = Math.max(0, Math.floor((rowHeight - pad * 2) / laneH + 0.001) - 1)
  if (state.event && state.toRowKey === state.fromRowKey) {
    const placements = layout.value.placements.get(state.fromRowKey) || []
    const placement = placements.find(p => p.event === state.event)
    if (placement) {
      return Math.min(placement.lane, maxLane)
    }
  }
  const el = scroller.value
  if (!el) {
    return 0
  }
  const rect = el.getBoundingClientRect()
  const y = state.clientY - rect.top + el.scrollTop - headerHeight - layout.value.offsets[index] - pad
  return Math.min(maxLane, Math.max(0, Math.floor(y / laneH)))
}

const ghostStyle = computed(() => {
  const state = drag.value
  if (!state) {
    return null
  }
  const index = flatRows.value.findIndex(r => r.key === state.toRowKey)
  if (index < 0) {
    return null
  }
  const left = timeToX(state.start.getTime(), columns.value, cellWidth.value)
  const right = timeToX(state.end.getTime(), columns.value, cellWidth.value)
  const lane = ghostLane(state, index)
  return {
    top: layout.value.offsets[index] + rowPadding.value + lane * laneHeight.value + 'px',
    left: effectiveLabelWidth.value + left + 'px',
    width: Math.max(right - left, 4) + 'px',
    height: laneHeight.value - 4 + 'px'
  }
})

const ghostLabel = computed(() => {
  const state = drag.value
  if (!state || !props.formatRange) {
    return ''
  }
  return props.formatRange(state.start, state.end)
})

// Horizontal window management

const firstVisibleIndex = () => {
  const el = scroller.value
  if (!el) {
    return buffer.value
  }
  return Math.floor(el.scrollLeft / cellWidth.value + 0.01)
}

const firstVisibleDate = (): Date | null => {
  const col = columns.value[Math.max(0, Math.min(columns.value.length - 1, firstVisibleIndex()))]
  return col ? col.date : null
}

let anchorEmitTimer: ReturnType<typeof setTimeout> | null = null
let lastEmittedAnchor: Date | null = null
let suppressAnchorEmit = false

const emitAnchorSoon = () => {
  if (anchorEmitTimer) {
    clearTimeout(anchorEmitTimer)
  }
  anchorEmitTimer = setTimeout(() => {
    anchorEmitTimer = null
    const date = firstVisibleDate()
    if (date && !isSameUnit(date, lastEmittedAnchor, props.unit)) {
      lastEmittedAnchor = date
      emit('update:anchor', date)
    }
  }, 150)
}

const scrollToTime = (time: number, offsetPx = 0) => {
  const el = scroller.value
  if (!el) {
    return
  }
  suppressAnchorEmit = true
  el.scrollLeft = Math.max(0, timeToX(time, columns.value, cellWidth.value) - offsetPx)
  updateXBucket()
  requestAnimationFrame(() => {
    suppressAnchorEmit = false
  })
}

let recenterPending = false
let positioned = false
const recenter = (time: number, offsetPx = 0) => {
  windowAnchor.value = startOfUnit(new Date(time), props.unit)
  recenterPending = true
  nextTick(() => {
    recenterPending = false
    scrollToTime(time, offsetPx)
    positioned = true
  })
}

const jumpTo = (date: Date) => {
  const start = startOfUnit(date, props.unit)
  lastEmittedAnchor = start
  recenter(start.getTime())
}

const recenterIfNearEdge = () => {
  const el = scroller.value
  if (!el || !positioned || recenterPending || isDragging() || props.fitRange) {
    return
  }
  const index = firstVisibleIndex()
  const margin = Math.max(2, Math.floor(buffer.value * 0.35))
  if (index < margin || index > windowCount.value - props.visibleColumns - margin) {
    const col = columns.value[Math.max(0, Math.min(columns.value.length - 1, index))]
    if (col) {
      const fraction = el.scrollLeft - index * cellWidth.value
      recenter(col.start, -fraction)
    }
  }
}

let touchCount = 0
let idleTimer: ReturnType<typeof setTimeout> | null = null
const scheduleIdle = () => {
  if (idleTimer) {
    clearTimeout(idleTimer)
  }
  idleTimer = setTimeout(() => {
    idleTimer = null
    if (touchCount > 0) {
      return
    }
    scrollActive = false
    recenterIfNearEdge()
    commitLayout(liveLayout.value)
  }, SCROLL_IDLE_MS)
}
const markScrollActive = () => {
  scrollActive = true
  scheduleIdle()
}

let scrollFrame: number | null = null
const onScroll = () => {
  if (!suppressAnchorEmit) {
    markScrollActive()
  }
  if (scrollFrame !== null) {
    return
  }
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = null
    const el = scroller.value
    if (!el) {
      return
    }
    scrollTop.value = el.scrollTop
    updateXBucket()
    if (!suppressAnchorEmit) {
      emitAnchorSoon()
    }
  })
}

watch(() => props.anchor, (value) => {
  if (!value) {
    return
  }
  const current = firstVisibleDate()
  if (!isSameUnit(value, current, props.unit)) {
    jumpTo(value)
  }
})

let pendingZoomAnchor: { time: number, offset: number } | null = null
watch([cellWidth, () => props.visibleColumns, () => props.unit], ([width, visible, unit], [oldWidth, oldVisible, oldUnit]) => {
  const el = scroller.value
  if (!el || isDragging()) {
    return
  }
  if (unit !== oldUnit) {
    jumpTo(props.anchor || new Date())
    return
  }
  if (visible !== oldVisible) {
    const anchor = pendingZoomAnchor
    pendingZoomAnchor = null
    if (anchor) {
      recenter(anchor.time, anchor.offset)
      return
    }
    // The scroll position still belongs to the old cell width and window, so read the visible date with those
    const oldStart = props.fitRange ? windowStart.value : addUnits(windowAnchor.value, unit, -bufferFor(oldVisible, unit))
    const index = oldWidth ? Math.floor(el.scrollLeft / oldWidth + 0.01) : 0
    recenter(addUnits(startOfUnit(oldStart, unit), unit, index).getTime(), 0)
    return
  }
  if (width !== oldWidth && oldWidth && !recenterPending) {
    const time = xToTime(el.scrollLeft, columns.value, oldWidth)
    nextTick(() => scrollToTime(time, 0))
  }
})

let lastRange = ''
watch([windowStart, windowCount], () => {
  const cols = columns.value
  if (!cols.length) {
    return
  }
  const key = cols[0].start + '-' + cols[cols.length - 1].end
  if (key === lastRange) {
    return
  }
  lastRange = key
  emit('range', { start: new Date(cols[0].start), end: new Date(cols[cols.length - 1].end) })
}, { immediate: true })

// Zoom gestures

const requestZoom = (direction: 'in' | 'out', clientX: number | null) => {
  const el = scroller.value
  if (el && clientX !== null) {
    const rect = el.getBoundingClientRect()
    const offset = clientX - rect.left - effectiveLabelWidth.value
    pendingZoomAnchor = { time: timeAtClientX(clientX), offset: Math.max(0, offset) }
  } else {
    pendingZoomAnchor = null
  }
  emit('zoom', { direction })
}

let wheelAccumulator = 0
const onWheel = (e: WheelEvent) => {
  if (!e.ctrlKey && !e.metaKey) {
    return
  }
  e.preventDefault()
  wheelAccumulator += e.deltaY
  if (Math.abs(wheelAccumulator) >= 60) {
    requestZoom(wheelAccumulator < 0 ? 'in' : 'out', e.clientX)
    wheelAccumulator = 0
  }
}

let pinchStart: number | null = null
// Touches that start inside an inline lens belong to the nested timeline and scroll natively
let lensTouch = false
const isLensTouch = (e: TouchEvent) => {
  const row = e.target instanceof Element ? e.target.closest('.st-row-custom') : null
  return !!row && row.closest('.st-scroller') === scroller.value
}
const touchDistance = (e: TouchEvent) => {
  const a = e.touches[0]
  const b = e.touches[1]
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}
const onTouchStart = (e: TouchEvent) => {
  if (e.touches.length === 1) {
    lensTouch = isLensTouch(e)
  }
  if (lensTouch) {
    return
  }
  touchCount = e.touches.length
  scrollActive = true
  pinchStart = e.touches.length === 2 ? touchDistance(e) : null
}
const onTouchEnd = (e: TouchEvent) => {
  if (lensTouch) {
    if (!e.touches.length) {
      lensTouch = false
    }
    return
  }
  touchCount = e.touches.length
  if (e.touches.length < 2) {
    pinchStart = null
  }
  if (!touchCount) {
    scheduleIdle()
  }
}
const onTouchMoveNative = (e: TouchEvent) => {
  if (lensTouch) {
    return
  }
  onTouchMove(e)
  if (e.touches.length !== 2 || pinchStart === null) {
    return
  }
  e.preventDefault()
  const distance = touchDistance(e)
  const ratio = distance / pinchStart
  if (ratio > 1.3 || ratio < 0.77) {
    const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2
    requestZoom(ratio > 1 ? 'in' : 'out', midX)
    pinchStart = distance
  }
}

// Labels

const onLabelClick = (row: FlatRow, e: MouseEvent) => {
  emit('label-click', row.row, e)
  if (row.hasChildren && !e.defaultPrevented) {
    emit('toggle', row.key, !row.open)
  }
}

const onLabelResizeStart = (e: PointerEvent) => {
  e.preventDefault()
  const startX = e.clientX
  const startWidth = effectiveLabelWidth.value
  const move = (ev: PointerEvent) => {
    const width = Math.max(100, Math.min(600, startWidth + ev.clientX - startX))
    emit('update:labelWidth', Math.round(width))
  }
  const up = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}

// Keyboard navigation between events

const focusPlacement = (p: Placement, retry = true) => {
  const el = root.value?.querySelector('[data-pkey="' + CSS.escape(p.key) + '"]') as HTMLElement | null
  if (el) {
    el.focus()
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    return
  }
  const sc = scroller.value
  if (sc && retry) {
    sc.scrollLeft = Math.max(0, p.left - (sc.clientWidth - effectiveLabelWidth.value) / 3)
    updateXBucket()
    requestAnimationFrame(() => nextTick(() => focusPlacement(p, false)))
  }
}

const onEventKey = (e: KeyboardEvent, p: Placement, rowIndex: number) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    emit('event-click', p.event, p.rowKey, e)
    return
  }
  const list = layout.value.placements.get(p.rowKey) || []
  const index = list.indexOf(p)
  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    e.preventDefault()
    const next = list[index + (e.key === 'ArrowRight' ? 1 : -1)]
    if (next) {
      focusPlacement(next)
    }
    return
  }
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    const step = e.key === 'ArrowDown' ? 1 : -1
    for (let i = rowIndex + step; i >= 0 && i < flatRows.value.length; i += step) {
      const candidates = layout.value.placements.get(flatRows.value[i].key)
      if (candidates && candidates.length) {
        let best = candidates[0]
        for (const c of candidates) {
          if (Math.abs(c.left - p.left) < Math.abs(best.left - p.left)) {
            best = c
          }
        }
        const el = scroller.value
        if (el) {
          const top = layout.value.offsets[i] + headerHeight
          if (top < el.scrollTop + headerHeight || top > el.scrollTop + el.clientHeight - 40) {
            el.scrollTop = Math.max(0, top - el.clientHeight / 2)
          }
        }
        requestAnimationFrame(() => nextTick(() => focusPlacement(best)))
        return
      }
    }
  }
}

// Lifecycle

let resizeObserver: ResizeObserver | null = null
let nowTimer: ReturnType<typeof setInterval> | null = null

const measure = () => {
  const el = scroller.value
  if (el) {
    viewportWidth.value = el.clientWidth
    viewportHeight.value = Math.max(0, el.clientHeight - headerHeight)
  }
  if (root.value) {
    rootWidth.value = root.value.clientWidth
  }
}

onMounted(() => {
  measure()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => measure())
    if (scroller.value) {
      resizeObserver.observe(scroller.value)
    }
    if (root.value) {
      resizeObserver.observe(root.value)
    }
  }
  scroller.value?.addEventListener('touchmove', onTouchMoveNative, { passive: false })
  nowTimer = setInterval(() => {
    now.value = Date.now()
  }, 60000)
  jumpTo(props.anchor || new Date())
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  scroller.value?.removeEventListener('touchmove', onTouchMoveNative)
  if (nowTimer) {
    clearInterval(nowTimer)
  }
  if (anchorEmitTimer) {
    clearTimeout(anchorEmitTimer)
  }
  if (idleTimer) {
    clearTimeout(idleTimer)
  }
})

defineExpose({
  scrollToTime,
  jumpTo,
  timeAt: timeAtClientX,
  getScrollTop: () => scroller.value?.scrollTop || 0,
  setScrollTop: (value: number) => {
    if (scroller.value) {
      scroller.value.scrollTop = value
    }
  }
})
</script>

<style scoped>
.st-root {
  --st-bg: #ffffff;
  --st-header-bg: #f8f9fb;
  --st-border: #e3e7ec;
  --st-grid: #eef1f4;
  --st-text: #26313d;
  --st-muted: #6b7785;
  --st-weekend: rgba(30, 41, 59, 0.035);
  --st-today: rgba(205, 220, 57, 0.2);
  --st-past: #9aa3ad;
  --st-past-hatch: rgba(30, 41, 59, 0.07);
  --st-past-bar: rgba(30, 41, 59, 0.18);
  --st-now: #e5484d;
  --st-accent: #3499db;
  --st-radius: 6px;
  --st-font: 'Oxygen', Arial, sans-serif;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  height: 60vh;
  background: var(--st-bg);
  border: 1px solid var(--st-border);
  border-radius: 8px;
  overflow: hidden;
  font-family: var(--st-font);
  color: var(--st-text);
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
.st-fit {
  min-height: 0;
  border: 0;
  border-radius: 0;
}
.st-row-custom {
  position: sticky;
  left: 0;
  z-index: 5;
  height: 100%;
  overflow: hidden;
}
.st-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 20;
  background: linear-gradient(90deg, transparent, var(--st-accent), transparent);
  background-size: 50% 100%;
  animation: st-loading 1.1s linear infinite;
}
@keyframes st-loading {
  from { background-position: -50% 0; }
  to { background-position: 150% 0; }
}
.st-scroller {
  position: relative;
  flex: 1;
  overflow: auto;
  overscroll-behavior: contain;
  touch-action: pan-x pan-y;
  -webkit-overflow-scrolling: touch;
}
.st-canvas {
  position: relative;
}
.st-header {
  position: sticky;
  top: 0;
  z-index: 6;
  display: flex;
  background: var(--st-header-bg);
  border-bottom: 1px solid var(--st-border);
}
.st-corner {
  position: sticky;
  left: 0;
  z-index: 7;
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px 0 8px;
  background: var(--st-header-bg);
  border-right: 1px solid var(--st-border);
  box-sizing: border-box;
}
.st-collapse {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: var(--st-muted);
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
}
.st-collapse:hover {
  background: rgba(0, 0, 0, 0.05);
}
.st-resize-handle {
  position: absolute;
  top: 0;
  right: -4px;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
}
.st-scale {
  position: relative;
  flex: none;
}
.st-scale-groups, .st-scale-columns {
  position: relative;
  height: 26px;
}
.st-scale-group {
  position: absolute;
  top: 0;
  height: 26px;
  border-left: 1px solid var(--st-border);
  box-sizing: border-box;
}
.st-scale-group-label {
  position: sticky;
  display: inline-block;
  padding: 0 8px;
  line-height: 26px;
  font-weight: 700;
  font-size: 12px;
  white-space: nowrap;
  text-transform: capitalize;
}
.st-scale-col {
  position: absolute;
  top: 0;
  height: 26px;
  line-height: 26px;
  text-align: center;
  font-size: 11px;
  color: var(--st-muted);
  border-left: 1px solid var(--st-grid);
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
}
.st-scale-col.st-weekend {
  background-color: var(--st-weekend);
}
.st-scale-col.st-today {
  background-color: var(--st-today);
  color: var(--st-text);
  font-weight: 700;
}
/* Past is marked in the date row only, so the events stay readable */
.st-scale-col.st-past {
  color: var(--st-past);
  background-image: repeating-linear-gradient(135deg, var(--st-past-hatch) 0 2px, transparent 2px 6px);
  box-shadow: inset 0 -3px 0 var(--st-past-bar);
}
.st-body {
  position: relative;
}
.st-grid {
  position: absolute;
  top: 0;
  z-index: 0;
  pointer-events: none;
  background-image: linear-gradient(to right, var(--st-grid) 1px, transparent 1px);
  background-repeat: repeat;
}
.st-stripe {
  position: absolute;
  top: 0;
  bottom: 0;
}
.st-stripe-weekend {
  background: var(--st-weekend);
}
.st-stripe-today {
  background: var(--st-today);
}
.st-now {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: var(--st-now);
  opacity: 0.7;
  z-index: 2;
}
.st-row {
  position: absolute;
  left: 0;
  display: flex;
  z-index: 1;
  border-bottom: 1px solid var(--st-grid);
  box-sizing: border-box;
}
.st-row.st-drop-target .st-track {
  background: rgba(52, 153, 219, 0.06);
}
.st-label {
  position: sticky;
  left: 0;
  z-index: 4;
  /* Android Chrome can paint promoted event layers over a sticky label that isn't composited itself */
  transform: translateZ(0);
  flex: none;
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 8px;
  background: var(--st-bg);
  border-right: 1px solid var(--st-border);
  box-sizing: border-box;
  overflow: hidden;
  font-size: 13px;
  cursor: default;
}
.st-folder .st-label {
  cursor: pointer;
}
.st-level-0 .st-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--st-text);
}
.st-level-0.st-folder .st-label {
  background: #f3f5f8;
}
.st-level-1 .st-label {
  font-size: 12px;
  font-weight: 600;
  color: #3b4754;
}
.st-level-2 .st-label, .st-level-3 .st-label, .st-level-4 .st-label {
  font-size: 12px;
  font-weight: 400;
  color: var(--st-muted);
}
.st-folder:hover .st-label {
  background: #edf1f5;
}
.st-toggle {
  flex: none;
  width: 16px;
  display: inline-flex;
  justify-content: center;
  color: var(--st-muted);
  font-size: 10px;
}
.st-toggle i {
  transition: transform 0.15s ease;
  transform: rotate(-90deg);
}
.st-toggle.st-open i {
  transform: none;
}
.st-label-content {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.st-narrow .st-label-content {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 11px;
  line-height: 1.2;
}
.st-collapsed .st-label-content {
  display: block;
  white-space: nowrap;
  font-size: 10px;
}
.st-track {
  position: relative;
  z-index: 0;
  isolation: isolate;
  flex: none;
  height: 100%;
  touch-action: pan-x pan-y;
}
.st-event {
  position: absolute;
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  border-radius: var(--st-radius);
  background: #e8f1fb;
  color: var(--st-text);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 3px solid var(--st-accent);
  font-size: 12px;
  line-height: 1.25;
  overflow: hidden;
  cursor: pointer;
  z-index: 1;
  transition: box-shadow 0.12s ease, opacity 0.12s ease;
  outline: none;
}
.st-event:hover {
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);
  z-index: 2;
}
.st-event:focus-visible {
  box-shadow: 0 0 0 2px var(--st-accent);
  z-index: 3;
}
.st-event-draggable {
  cursor: grab;
}
.st-dragging .st-event-draggable {
  cursor: grabbing;
}
.st-event-dragging {
  opacity: 0.35;
}
.st-clipped-start {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.st-clipped-end {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.st-event-body {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.st-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  z-index: 2;
  cursor: ew-resize;
  touch-action: none;
}
.st-handle-start {
  left: 0;
}
.st-handle-end {
  right: 0;
}
.st-event:hover .st-handle-end::after, .st-event:hover .st-handle-start::after {
  content: '';
  position: absolute;
  top: 25%;
  bottom: 25%;
  left: 3px;
  width: 2px;
  border-radius: 1px;
  background: rgba(0, 0, 0, 0.25);
}
@media (pointer: coarse) {
  .st-handle {
    width: 14px;
  }
}
.st-ghost {
  position: absolute;
  z-index: 3;
  box-sizing: border-box;
  border-radius: var(--st-radius);
  border: 2px dashed var(--st-accent);
  background: rgba(52, 153, 219, 0.14);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.18);
  pointer-events: none;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.st-ghost-create {
  border-style: solid;
  background: rgba(118, 184, 65, 0.18);
  border-color: #76b841;
}
.st-ghost-label {
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.st-skew .st-event.st-slant {
  transform: skewX(-20deg);
  transform-origin: bottom left;
}
.st-skew .st-slant .st-event-body {
  transform: skewX(20deg);
  padding: 0 10px;
}
.st-fit .st-event,
.st-fit .st-event-body {
  transform: none;
}
.st-compact .st-event {
  font-size: 11px;
}
.st-relaxed .st-event {
  font-size: 14px;
}
</style>
